import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import { BehaviorSubject } from 'rxjs';

import { SelectOption } from '../select-field/select-field.component';
import { RouterLink } from './types/router-link.type';
import { TableListAction } from './table-list-action/table-list-action.type';
import { TableListData } from './lib/table-list-data';
import { TableListDataSource } from './lib/table-list-data-source';
import { TableListModel } from './lib/table-list-model';
import { TableListRow } from './lib/table-list-row';
import { TableListSchema } from './lib/table-list-schema';

class TableListController {
  static $inject = ['$window', '$state'];

  private model!: TableListModel;
  private data!: TableListData[];
  private links!: string[];
  private actions!: TableListAction[][];
  private actionsOnHoverEnabled!: boolean;
  private options!: SelectOption[][];
  private routerLinks!: RouterLink<any>[];
  private dropdownEnabled = false;
  private dataSource = new TableListDataSource<TableListData>();
  private dataStream$: BehaviorSubject<TableListData[]> = this.dataSource.connect();
  private headerRow$ = new BehaviorSubject<TableListRow>({ hash: null, values: null });
  private onRowDropdownInit!: ($args: { $index: number }) => SelectOption[];
  private onRowDropdownSelect!: ($args: { $option: SelectOption; $index: number; $options: Object }) => void;
  private onRowActionSelect!: ($args: { $selection: TableListAction; $index: number }) => void;
  private schema!: TableListSchema;

  constructor(private $window: angular.IWindowService, private $state: ngRouter.StateService) {}

  $onChanges(changed: angular.IOnChangesObject) {
    let tableListModel: TableListModel;

    if (changed['data'] || changed['schema']) {
      tableListModel = new TableListModel({
        data: this.data,
        schema: this.schema,
      });

      this.headerRow$.next(tableListModel.headerRow());
      this.dataSource.data(tableListModel.bodyRows());
    }
  }

  hasLink($index: number): boolean {
    const hasLinks = this.links && this.links[$index] != null;
    const hasRouterLinks = this.routerLinks && this.routerLinks[$index] != null;

    return !!(hasLinks || hasRouterLinks);
  }

  hasLinks(): boolean {
    const hasLinks = this.links && this.links.length > 0;
    const hasRouterLinks = this.routerLinks && this.routerLinks.length > 0;

    return !!(hasLinks || hasRouterLinks);
  }

  getRowActions($index: number): TableListAction[] {
    return this.actions && this.actions[$index];
  }

  getRowDropdownOptions($index: number): SelectOption[] {
    return (this.options && this.options[$index]) || this.onRowDropdownInit({ $index });
  }

  handleRowOptionSelection($option: SelectOption, $index: number, $options: any) {
    if ($option.value != null) {
      // null value options are reserved for things like dividers
      this.onRowDropdownSelect({ $option, $index, $options });
    }
  }

  handleRowActionSelection($selection: TableListAction, $index: number) {
    if ($selection.value != null) {
      this.onRowActionSelect({ $selection, $index });
    }
  }

  handleRowClick($index: number): void {
    if (this.routerLinks) {
      this.$state.go(this.routerLinks[$index].stateOrName, this.routerLinks[$index].params);
    } else {
      this.$window.location.href = this.links[$index];
    }
  }

  isRouterLink(link: any) {
    return !!(link['path'] && link['path'].length);
  }

  getRowClasses(data: TableListData, $index: any) {
    let classes: any = {
      'row-link': this.hasLink($index),
      'row-actions-on-hover': this.actionsOnHoverEnabled === true,
    };

    if (data.options && data.options.styleClasses) {
      classes = [classes, ...data.options.styleClasses];
    }

    return classes;
  }
}

export const TableListComponent: angular.IComponentOptions = {
  controller: TableListController,
  controllerAs: 'ctrl',
  bindings: {
    actions: '<',
    actionsEnabled: '<',
    actionsOnHoverEnabled: '<',
    onRowActionSelect: '&',
    onRowDropdownInit: '&',
    onRowDropdownSelect: '&',
    dropdownEnabled: '<',
    dropdownTooltipMessage: '<',
    dropdownTooltipEnabled: '<',
    data: '<',
    links: '<',
    routerLinks: '<',
    schema: '<',
    options: '<',
    headerStyleCasual: '<',
  },
  template: `
    <table-list-header
      ng-class="{ 'table-list-header-casual': ctrl.headerStyleCasual }"
      actions-enabled="ctrl.actionsEnabled"
      data="(ctrl.headerRow$ | async:this).values"
      dropdown-enabled="ctrl.dropdownEnabled"
      has-links="ctrl.hasLinks()" />
    <table-list-row
      ng-class="ctrl.getRowClasses(row, $index)"
      ng-click="ctrl.hasLink($index) && ctrl.handleRowClick($index)"
      ng-repeat="row in (ctrl.dataStream$ | async:this) track by row.hash+$index"
      actions="ctrl.getRowActions($index)"
      actions-enabled="ctrl.actionsEnabled"
      dropdown-enabled="ctrl.dropdownEnabled"
      dropdown-tooltip-message="ctrl.dropdownTooltipMessage"
      dropdown-tooltip-enabled="ctrl.dropdownTooltipEnabled"
      has-link="ctrl.hasLink($index)"
      options="ctrl.getRowDropdownOptions($index)"
      on-action-select="ctrl.handleRowActionSelection($selection, $index)"
      on-select="ctrl.handleRowOptionSelection($option, $index, $options)"
      data="row.values"
      data-schema="{{ctrl.schema.name}}"
      tabindex="{{ctrl.hasLink($index) ? 0 : -1}}"
      />
  `,
};
